import React, { useState } from 'react';
import { ArrowsRightLeftIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const MergeAccountModal = ({ isOpen, onClose, sourceAccount, allAccounts, onMerge }) => {
  const [search, setSearch] = useState('');
  const [targetAccount, setTargetAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !sourceAccount) return null;

  const filtered = allAccounts.filter(a =>
    a.id !== sourceAccount.id &&
    a.Name?.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 10);

  const handleMerge = async () => {
    if (!targetAccount) return;
    setIsLoading(true);
    setError('');
    try {
      await onMerge(sourceAccount.id, targetAccount.id);
      onClose();
    } catch (e) {
      setError(e.message || 'Merge failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-orange-100 rounded-lg">
            <ArrowsRightLeftIcon className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Merge Account</h2>
            <p className="text-sm text-gray-500">All enquiries from source will move to target</p>
          </div>
        </div>

        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-xs text-red-500 font-medium uppercase mb-1">Source (will be deleted)</p>
          <p className="font-medium text-gray-900">{sourceAccount.Name}</p>
        </div>

        <div className="flex justify-center mb-4">
          <ArrowsRightLeftIcon className="w-5 h-5 text-gray-400" />
        </div>

        <div className="mb-4">
          <p className="text-xs text-green-600 font-medium uppercase mb-2">Target (keep this one)</p>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search account to merge into..."
              value={search}
              onChange={e => { setSearch(e.target.value); setTargetAccount(null); }}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
          {search && (
            <div className="mt-1 border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="p-3 text-sm text-gray-500">No accounts found</p>
              ) : (
                filtered.map(a => (
                  <button
                    key={a.id}
                    onClick={() => { setTargetAccount(a); setSearch(a.Name); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                  >
                    {a.Name}
                    {a.Location && <span className="text-gray-400 ml-2 text-xs">{a.Location}</span>}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {targetAccount && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-xs text-green-600 font-medium uppercase mb-1">Merging into</p>
            <p className="font-medium text-gray-900">{targetAccount.Name}</p>
            <p className="text-xs text-gray-500 mt-1">
              "{sourceAccount.Name}" will be saved as an alias so searches still find it
            </p>
          </div>
        )}

        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleMerge}
            disabled={!targetAccount || isLoading}
            className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Merging...' : 'Merge'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MergeAccountModal;
